import React, {useState} from 'react'
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
} from '@mui/material'
import {styled} from '@mui/material/styles'
import {findWalletBalance, formatNumber} from '../helpers/UtilityHelper'
import {apiConstants} from '../common/constants'
import {useGenericPostRequestQuery} from '../redux/commonRTK'
import {
  CloseTwoTone,
  WalletTwoTone,
  AccountBalanceWallet,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material'
import {red, green, grey} from '@mui/material/colors'
import {useForm} from 'react-hook-form'
import {FormProvider} from '../common/hook-form'
import {useWallet} from './useWallet'
import {LoadingButton} from '@mui/lab'

const rechargeAmountList = [500, 1000, 2000, 5000, 10000]

const StyledWalletButton = styled(Button)(({theme}) => ({
  backgroundColor: red[50],
  boxShadow: 'none',
  color: red[800],
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0.4, 1.25),
  fontSize: theme.typography.caption.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  minHeight: 0,
  lineHeight: 1.5,
  '&:hover': {
    backgroundColor: red[100],
  },
}))

const StyledDialog = styled(Dialog)(({theme}) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(2),
    maxWidth: 700,
    width: '100%',
    margin: theme.spacing(2),
  },
}))

const StyledDialogTitle = styled(DialogTitle)(({theme}) => ({
  padding: theme.spacing(2, 3),
  backgroundColor: 'white',
  borderBottom: `1px solid ${grey[200]}`,
}))

const StyledDialogContent = styled(DialogContent)(({theme}) => ({
  padding: theme.spacing(3),
}))

const StyledAmountInput = styled(Box)(({theme}) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(1),
  width: '60%',
  margin: '0 auto',
}))

const StyledAmountText = styled(Typography)(({theme}) => ({
  fontSize: '2.5rem',
  fontWeight: 'bold',
  color: theme.palette.text.primary,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0,
  marginBottom: theme.spacing(1),
}))

const StyledMinMaxText = styled(Typography)(({theme}) => ({
  color: '#ff6b6b',
  fontSize: '0.875rem',
  marginBottom: theme.spacing(2),
}))

const StyledChip = styled(Chip)(({theme}) => ({
  margin: theme.spacing(0.5),
  borderRadius: theme.spacing(3),
  border: `1px solid ${grey[300]}`,
  backgroundColor: 'white',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.dark,
    borderColor: theme.palette.primary.main,
  },
}))

const StyledSummaryCard = styled(Card)(({theme}) => ({
  backgroundColor: grey[50],
  border: 'none',
  boxShadow: 'none',
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
}))

const StyledPayButton = styled(LoadingButton)(({theme}) => ({
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1.5),
  width: '100%',
  fontSize: '1rem',
  fontWeight: 600,
  '&:hover': {
    backgroundColor: grey[500],
  },
  '&:disabled': {
    backgroundColor: grey[300],
    color: grey[500],
  },
}))

const WalletDetails = ({merchantId}) => {
  const [open, setOpen] = useState(false)
  const [showSummary, setShowSummary] = useState(false)

  const {data: merchant, refetch: refetchWallet} = useGenericPostRequestQuery({
    type: 'GET',
    url: `${apiConstants.GET_ALL_WALLET}`,
    data: {
      merchantId,
    },
  })

  const method = useForm({
    shouldUnregister: true,
  })
  const {handleSubmit, watch} = method
  const amountValue = watch('amount')

  const walletDetails = merchant ? findWalletBalance(merchant) : {}
  const {makePayment, loading} = useWallet(walletDetails, () => {
    refetchWallet()
    setOpen(false)
    method.reset()
  })

  const onSubmitHandler = (data) => {
    makePayment(data.amount)
  }

  const inr = (v) => Intl.NumberFormat('en-IN', {style: 'currency', currency: 'INR'}).format(v || 0)
  // Money held against open weight discrepancies is not spendable, so the chip shows what the merchant
  // can actually use. The held amount itself is shown in the Weight Discrepancies pop-up (WdInfo.js):
  // next to the balance it read as a deduction (business, 22 Oct). Hovering the chip still explains it.
  const heldAmount = Number(walletDetails.heldAmount || 0)
  const available = walletDetails.availableBalance != null
    ? walletDetails.availableBalance
    : Number(walletDetails.walletBalance || 0) - heldAmount
  const getWalletBalance = () => inr(available)

  if (walletDetails?.walletType !== 'Prepaid') return null

  const numericAmount = parseFloat(amountValue) || 0

  return (
    <>
      <StyledWalletButton
        onClick={() => setOpen(true)}
        variant='contained'
        size='small'
        startIcon={<WalletTwoTone sx={{fontSize: 16}} />}
        title={heldAmount > 0 ? `Available ${inr(available)}. ${inr(heldAmount)} is held against open weight discrepancies.` : undefined}
      >
        {getWalletBalance()}
      </StyledWalletButton>

      <StyledDialog open={open} onClose={() => setOpen(false)} scroll='body'>
        <FormProvider methods={method} onSubmit={handleSubmit(onSubmitHandler)}>
          <StyledDialogTitle>
            <Box display='flex' alignItems='center' justifyContent='space-between'>
              <Typography variant='h6' fontWeight={600}>
                Recharge Your Proship Wallet
              </Typography>
              <Box display='flex' alignItems='center' gap={1}>
                <AccountBalanceWallet fontSize='small' sx={{color: green[600]}} />
                <Typography variant='body1' fontWeight={600} color={green[600]}>
                  {getWalletBalance()}
                </Typography>
              </Box>
            </Box>
          </StyledDialogTitle>

          <StyledDialogContent>
            <Box textAlign='center' my={2}>
              <Typography variant='body2' color='text.secondary' gutterBottom>
                Enter Amount to Add
              </Typography>

              <StyledAmountInput>
                <StyledAmountText>
                  <TextField
                    name='amount'
                    value={amountValue ? `₹  ${amountValue}` : '₹'}
                    onChange={(e) => {
                      const value = e.target.value.replace('₹', '').replace(/[^0-9]/g, '')
                      method.setValue('amount', value)
                    }}
                    onKeyPress={(e) => {
                      if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                        e.preventDefault()
                      }
                    }}
                    variant='standard'
                    placeholder='₹0'
                    InputProps={{
                      disableUnderline: true,
                      style: {
                        fontSize: '2.5rem',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        border: 'none',
                        margin: 0,
                        padding: 0,
                      },
                    }}
                    sx={{
                      margin: 0,
                      padding: 0,
                      '& .MuiInputBase-root': {
                        margin: 0,
                        padding: 0,
                      },
                      '& .MuiInputBase-input': {
                        textAlign: 'center',
                        border: 'none',
                        outline: 'none',
                        minWidth: '150px',
                        margin: 0,
                        padding: 0,
                      },
                    }}
                  />
                  {amountValue > 0 && (
                    <IconButton size='small' onClick={() => method.setValue('amount', '')}>
                      <CloseTwoTone fontSize='small' />
                    </IconButton>
                  )}
                </StyledAmountText>
              </StyledAmountInput>

              <StyledMinMaxText>Min amount: ₹500</StyledMinMaxText>
            </Box>

            <Box textAlign='center' mb={3}>
              {rechargeAmountList.map((amount) => (
                <StyledChip
                  key={amount}
                  label={`₹${formatNumber(amount)}`}
                  variant='outlined'
                  size='small'
                  clickable
                  onClick={() => method.setValue('amount', amount)}
                />
              ))}
            </Box>

            <StyledSummaryCard>
              <CardContent sx={{py: 2}}>
                <Box display='flex' alignItems='center' justifyContent='space-between' mb={1}>
                  <Box display='flex' alignItems='center' gap={1}>
                    <AccountBalanceWallet fontSize='small' color='action' />
                    <Typography variant='body2'>
                      Amount to be recharged: ₹
                      {numericAmount > 0 ? formatNumber(numericAmount) : '0'}
                    </Typography>
                  </Box>
                  <IconButton size='small' onClick={() => setShowSummary(!showSummary)}>
                    {showSummary ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                </Box>

                {showSummary && numericAmount > 0 && (
                  <Box>
                    <Divider sx={{my: 1}} />
                    <Box display='flex' justifyContent='space-between' mb={1}>
                      <Typography variant='body2'>Recharge Amount</Typography>
                      <Typography variant='body2'>₹{formatNumber(numericAmount)}</Typography>
                    </Box>
                    <Box display='flex' justifyContent='space-between'>
                      <Typography variant='body2' fontWeight={600}>
                        Payable Amount
                      </Typography>
                      <Typography variant='body2' fontWeight={600}>
                        ₹{formatNumber(numericAmount)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </StyledSummaryCard>
          </StyledDialogContent>

          <DialogActions sx={{p: 3}}>
            <StyledPayButton
              loading={loading}
              disabled={!(numericAmount >= 500 && numericAmount <= 5000000)}
              variant='contained'
              type='submit'
            >
              Pay ₹{numericAmount > 0 ? formatNumber(numericAmount) : '0'}
            </StyledPayButton>
          </DialogActions>
        </FormProvider>
      </StyledDialog>
    </>
  )
}

export default WalletDetails
